import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { Tab, TabType } from '../../../context/tab-manager-context';
import { getTabDisplayName } from '../../../utils/tab-display';
import { ConfirmCloseModal } from '../ConfirmCloseModal';

import { TabItemMenu } from './TabItemMenu';

import { Icon } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';

interface TabItemV3Props {
  tab: Tab;
  isDirty?: boolean;
  currentUserId?: number;
  description?: string;
  onClick?: (tabId: string) => void;
  isActive?: boolean;
  isDropdownOpen?: boolean;
  onDropdownToggle?: (tabId: string) => void;
  onCloseDropdown?: () => void;
  onCloseTab?: (tab: Tab) => void;
}

const TabContainer = styled.div<{ isActive: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  height: 35px;
  padding: 0 8px;
  cursor: pointer;
  max-width: 200px;
  min-width: 150px;
  flex-shrink: 0;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.v2.tab.activeBackgroundColor : 'transparent'};
  border-bottom: ${({ theme, isActive }) =>
    isActive
      ? `2px solid ${theme.v2.tab.activeBorderColor}`
      : `2px solid ${theme.v2.tab.inactiveBorderColor}`};
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.hoverBackgroundColor};
  }
  &:hover .tab-actions {
    opacity: 1;
    pointer-events: auto;
  }
`;

const TabContent = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  flex-wrap: nowrap;
  padding-right: 0;
`;

const DirtyIndicator = styled.span`
  width: 16px;
  flex-shrink: 0;
  color: ${({ theme }) => theme.v2.tab.dirtyIndicatorColor};
  text-align: center;
`;

const IconWrapper = styled.span`
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TabName = styled.span<{ isActive?: boolean }>`
  /* CR/13 Data */
  font-family: 'Open Sans';
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
  color: ${({ theme, isActive }) =>
    isActive ? theme.v2.tab.tabSelectedText : theme.v2.tab.textColor};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
`;

const ActionsWrapper = styled.div`
  position: absolute;
  top: 0;
  right: 4px;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 2px;
  padding-left: 32px;
  background: linear-gradient(
    to right,
    transparent 0%,
    ${({ theme }) => theme.v2.tab.hoverBackgroundColor || 'rgba(255,255,255,0.05)'} 60%,
    ${({ theme }) =>
        theme.v2.tab.hoverBackgroundColor || theme.v2.tab.containerBackgroundColor || 'inherit'}
      100%
  );
  opacity: 0;
  transition: opacity 0.15s ease;
  pointer-events: none;
`;

const ChevronWrapper = styled.div`
  position: relative;
`;

const CloseButton = styled.button`
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  border-radius: 4px;
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.dropdownHoverColor};
  }
`;

const DropdownTrigger = styled.button`
  width: 20px;
  height: 18px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.dropdownHoverColor};
    border-radius: 4px;
  }
`;

const getTabIcon = (tabType?: TabType): string => {
  if (!tabType) return ICONS.Views;
  const iconMap: Record<TabType, string> = {
    ALERT_BOARD: ICONS.Alert,
    VIEWS: ICONS.Views,
    ALERT_MANAGER: ICONS.Gear,
    LIMIT_MANAGER: ICONS.Limits,
    MANAGE: ICONS.Manage,
  };
  return iconMap[tabType] || ICONS.Views;
};

export const TabItemV3: React.FC<TabItemV3Props> = ({
  tab,
  isDirty = false,
  currentUserId,
  onClick,
  isActive = false,
  isDropdownOpen = false,
  onDropdownToggle,
  onCloseDropdown,
  onCloseTab,
}) => {
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(
    null,
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        onCloseDropdown?.();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen, onCloseDropdown]);

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
      });
    }
    onDropdownToggle?.(tab.id);
  };

  const handleCloseDropdown = () => {
    onCloseDropdown?.();
  };

  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  const handleCloseTab = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isDirty) {
      setShowCloseConfirm(true);
      return;
    }
    onCloseTab?.(tab);
  };

  const handleCloseConfirm = (confirmed: boolean) => {
    setShowCloseConfirm(false);
    if (confirmed) {
      onCloseTab?.(tab);
    }
  };

  const tabIcon = getTabIcon(tab.type);

  const tabContent = (
    <>
      {isDirty && <DirtyIndicator>*</DirtyIndicator>}
      <IconWrapper>
        <Icon name={tabIcon} width="14" height="14" />
      </IconWrapper>
      <TabName title={getTabDisplayName(tab)} isActive={isActive}>
        {getTabDisplayName(tab)}
      </TabName>
    </>
  );

  return (
    <TabContainer
      isActive={isActive}
      onClick={(e) => {
        e.stopPropagation();
        if (onClick) {
          onClick(tab.id);
        }
      }}
    >
      <TabContent>{tabContent}</TabContent>
      <ActionsWrapper className="tab-actions">
        {onCloseTab && (
          <CloseButton
            type="button"
            onClick={handleCloseTab}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Close tab"
            data-testid={`tab-close-${tab.id}`}
          >
            <Icon name={ICONS.Close} width="10" height="10" color="#A0A38D" />
          </CloseButton>
        )}
        <ChevronWrapper>
          <DropdownTrigger
            ref={triggerRef}
            onClick={handleDropdownToggle}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Tab menu"
            data-testid={`tab-menu-${tab.id}`}
          >
            <Icon name={ICONS.Chevron} width="10" color="#A0A38D" />
          </DropdownTrigger>
          <TabItemMenu
            open={isDropdownOpen}
            position={dropdownPosition}
            dropdownRef={dropdownRef}
            tab={tab}
            isDirty={isDirty}
            currentUserId={currentUserId}
            onClose={handleCloseDropdown}
          />
        </ChevronWrapper>
      </ActionsWrapper>
      {showCloseConfirm && <ConfirmCloseModal onConfirm={handleCloseConfirm} />}
    </TabContainer>
  );
};
